import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Copy, Gift, CreditCard, Clock, Check, Loader2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const F = { mono: "'IBM Plex Mono',monospace" };

const STATUS_COPY = {
  trialing: { label: 'Free trial', color: '#00A8CC' },
  active: { label: 'Active', color: '#06D6A0' },
  extended_credit: { label: 'Extended by referral credit', color: '#06D6A0' },
  past_due: { label: 'Payment needed', color: '#FF9F43' },
  canceled: { label: 'Canceled', color: '#FF4444' },
};

export default function BillingPage() {
  const { colors: T, isDark } = useTheme();
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/api/billing/subscription`, { withCredentials: true }),
        axios.get(`${API}/api/referrals/me`, { withCredentials: true }),
      ]);
      setBilling(b.data);
      setReferrals(r.data);
    } catch (e) {
      toast.error('Could not load your billing details.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyCredits = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/billing/credits/apply`, {}, { withCredentials: true });
      toast.success(`${data.applied_days} free days added to your account.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply your credits.');
    } finally {
      setBusy(false);
    }
  };

  const upgrade = async planId => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/payments/checkout`,
        { plan_id: planId, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  const sub = billing?.subscription;
  const status = STATUS_COPY[sub?.status] || STATUS_COPY.trialing;
  const shareUrl = referrals ? `${window.location.origin}/auth?ref=${referrals.referral_code}` : '';

  const card = {
    background: T.surfaceAlpha, border: `1px solid ${T.border}`, borderRadius: 10,
    padding: 22, boxShadow: T.elev1,
  };
  const h2 = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: T.muted, marginBottom: 14 };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text }}>
      <header style={{ borderBottom: `1px solid ${T.border}`, padding: '14px 20px', background: T.navBg }}>
        <div style={{ maxWidth: 940, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/dashboard" data-testid="billing-back"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.muted, textDecoration: 'none', fontSize: 13 }}>
            <ArrowLeft size={15} /> Dashboard
          </Link>
          <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0, letterSpacing: '-.02em' }}>Billing & Referrals</h1>
        </div>
      </header>

      <main id="main" style={{ maxWidth: 940, margin: '0 auto', padding: '24px 20px 60px', display: 'grid', gap: 18 }}>
        {!billing ? (
          <div style={{ padding: 60, textAlign: 'center', color: T.muted }}>
            <Loader2 size={22} className="animate-spin" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <>
            {/* Plan & trial */}
            <section style={card} data-testid="billing-subscription">
              <div style={h2}>Your plan</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
                <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.02em' }}>
                  {(user?.plan || 'trial').charAt(0).toUpperCase() + (user?.plan || 'trial').slice(1)}
                </span>
                <span data-testid="billing-status" style={{
                  fontSize: 12, fontFamily: F.mono, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
                  background: `${status.color}18`, border: `1px solid ${status.color}55`, color: status.color,
                }}>{status.label}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: T.textSecondary }}>
                <Clock size={15} color={T.muted} />
                {sub?.days_remaining === null ? 'No active period.'
                  : sub?.status === 'active' ? `Renews in ${sub.days_remaining} days.`
                  : sub?.status === 'extended_credit' ? `${sub.days_remaining} days of access left, including your referral credit.`
                  : sub?.days_remaining > 0 ? `${sub.days_remaining} of your ${billing.trial_days} trial days left.`
                  : 'Your trial has ended.'}
              </div>

              {!sub?.card_on_file && (
                <div data-testid="billing-card-notice" style={{
                  marginTop: 14, padding: '12px 14px', borderRadius: 8, fontSize: 13, lineHeight: 1.55,
                  background: 'rgba(255,159,67,.09)', border: '1px solid rgba(255,159,67,.32)', color: isDark ? '#FFC98B' : '#9A5B00',
                  display: 'flex', gap: 10,
                }}>
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    <strong>No card on file.</strong> Card-on-file trials need a Stripe account connected to this
                    workspace. Until then your trial runs without a card and you pay when you choose a plan.
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
                {['starter', 'pro', 'enterprise'].map(p => (
                  <button key={p} data-testid={`billing-upgrade-${p}`} onClick={() => upgrade(p)} disabled={busy}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', borderRadius: 999,
                      border: 'none', cursor: busy ? 'progress' : 'pointer', fontSize: 13, fontWeight: 600,
                      color: '#fff', background: p === 'pro' ? '#002FA7' : T.muted, opacity: busy ? 0.6 : 1,
                    }}>
                    <CreditCard size={15} /> {p === 'starter' ? 'Starter $9.98' : p === 'pro' ? 'Pro $49' : 'Enterprise $199'}
                  </button>
                ))}
              </div>
            </section>

            {/* Referrals */}
            <section style={card} data-testid="billing-referrals">
              <div style={h2}>Refer & earn</div>
              <p style={{ fontSize: 14, color: T.textSecondary, lineHeight: 1.6, marginTop: 0, marginBottom: 16 }}>
                Share your link. When someone you refer makes their first payment after their trial,
                you get <strong>{referrals?.reward_days_per_conversion || 30} days free</strong> — no cap.
              </p>

              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input data-testid="referral-link" readOnly value={shareUrl} onFocus={e => e.target.select()}
                  aria-label="Your referral link" title={shareUrl}
                  style={{
                    flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 8, fontSize: 13, fontFamily: F.mono,
                    background: T.bg, border: `1px solid ${T.border}`, color: T.text,
                  }} />
                <button data-testid="copy-referral" onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success('Referral link copied.');
                  } catch {
                    toast.error('Could not copy automatically \u2014 select the link and copy it.');
                  }
                }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 8,
                    border: '1px solid rgba(0,47,167,.3)', background: 'rgba(0,47,167,.09)', color: '#002FA7',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  <Copy size={14} /> Copy
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
                {[
                  { label: 'Referred', value: referrals?.total_referrals ?? 0, testId: 'ref-total' },
                  { label: 'Converted to paid', value: referrals?.converted ?? 0, testId: 'ref-converted' },
                  { label: 'Free days earned', value: referrals?.credit_days_balance ?? 0, testId: 'ref-credits' },
                ].map(s => (
                  <div key={s.label} data-testid={s.testId}
                    style={{ padding: '14px 16px', borderRadius: 8, background: T.bg, border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 24, fontWeight: 700, fontFamily: F.mono }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: T.muted }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {(referrals?.credit_days_balance ?? 0) > 0 && (
                <button data-testid="apply-credits" onClick={applyCredits} disabled={busy}
                  style={{
                    marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px',
                    borderRadius: 999, border: 'none', background: '#06D6A0', color: '#04241a',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}>
                  <Gift size={15} /> Apply {referrals.credit_days_balance} free days
                </button>
              )}

              {referrals?.referrals?.length > 0 && (
                <table style={{ width: '100%', marginTop: 20, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: T.muted }}>
                      <th style={{ padding: '8px 0', fontWeight: 600 }}>Signed up</th>
                      <th style={{ padding: '8px 0', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '8px 0', fontWeight: 600 }}>Reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.referrals.map(r => (
                      <tr key={r.created_at} style={{ borderTop: `1px solid ${T.border}` }}>
                        <td style={{ padding: '10px 0' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                        <td style={{ padding: '10px 0', textTransform: 'capitalize' }}>{r.status.replace('_', ' ')}</td>
                        <td style={{ padding: '10px 0' }}>
                          {r.reward_applied_at
                            ? <span style={{ color: '#06D6A0', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <Check size={13} /> {r.reward_days_awarded} days
                              </span>
                            : <span style={{ color: T.muted }}>Pending first payment</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
